import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import { FC, useContext } from 'react';

import { Currency, Money } from '@catawiki/runtime-toolkit';
import { LotCard, useI18n } from '@catawiki/cata-style';

import { useRegistrationHandlers } from '@/shared/hooks/useRegistrationHandlers';
import { EnrichedLot } from '@/shared/models/lot';
import { BiddingCurrency } from '@/shared/models/money';
import { trackEvent } from '@/shared/utils/tracking';
import {
  REGISTRATION_DIALOGUE_SEEN_SOURCE_EVENT_NAME,
  RegistrationDialogueSource,
} from '@/shared/utils/tracking/registrationDialogSeenSource';

import {
  LoadingState,
  UserLotFavoritesContext,
} from '../../providers/UserLotFavoritesProvider';
import { UserContext } from '../../providers/UserProvider';

interface Props {
  lot: EnrichedLot;
}

const FavoriteChipNoSSR = dynamic(
  () => import('@catawiki/cata-style').then((module) => module.FavoriteChip),
  {
    ssr: false,
  }
);

const convertToLotCardLot = (lot: EnrichedLot, statusText: string) => ({
  title: lot.title,
  imageUrl:
    lot.imageUrl ||
    'https://assets.catawiki.nl/assets/2019/6/23/b/a/0/ba066cc6-ecc2-46d8-bb97-5e09ac250a77.jpg',
  lotUrl: lot.lotUrl,
  status: undefined,
  statusText,
  lazyLoad: true,
});

export const LotCardItem: FC<Props> = ({ lot }) => {
  const { t } = useTranslation();
  const i18n = useI18n();
  const { onSignInHandler } = useRegistrationHandlers();
  const userContext = useContext(UserContext);
  const { lotFavorites, addFavorite, deleteFavorite, state } = useContext(
    UserLotFavoritesContext
  );
  const userCurrencyCode = userContext.currentUser.currencyCode;
  const toggleFavorite = () => {
    if (lotFavorites[lot.id]?.isCurrentUserFavorite) {
      return deleteFavorite?.(lot.id);
    }
    return addFavorite?.(lot.id);
  };

  const lotStatusText = lot.closed
    ? t('similar_lots.sold_for')
    : t('short_auction_current_bid');

  const lotPriceInUserCurrency = (
    enrichedLot: EnrichedLot,
    biddingCurrency: BiddingCurrency
  ) => {
    if (biddingCurrency === Currency.Currency.EURO) {
      return enrichedLot.price;
    }

    if (enrichedLot.lotPriceInCurrencies) {
      return Money.toCurrency(
        i18n,
        enrichedLot.lotPriceInCurrencies.current_bid_amount[biddingCurrency],
        biddingCurrency,
        {
          precision: 0,
        }
      );
    }

    return enrichedLot.price;
  };

  const displayedLotPrice = lotPriceInUserCurrency(lot, userCurrencyCode);

  return (
    <LotCard
      favoriteButton={
        <FavoriteChipNoSSR
          onClick={(e) => {
            if (userContext.currentUser.isLogged) {
              toggleFavorite();
              return;
            }
            trackEvent({
              event: REGISTRATION_DIALOGUE_SEEN_SOURCE_EVENT_NAME,
              eventPayload: {
                source: RegistrationDialogueSource.FAVORITE_LOT_HEART_BUTTON,
              },
            });
            onSignInHandler(e);
          }}
          isFavorite={lotFavorites[lot.id]?.isCurrentUserFavorite}
          count={lotFavorites[lot.id]?.count}
          disabled={state !== LoadingState.IDLE}
          variant="primary"
        />
      }
      lot={convertToLotCardLot(lot, lotStatusText)}
      price={displayedLotPrice}
    />
  );
};
